import { makeObservable, observable } from 'mobx'

import { GOFCategory } from '../constants'
import { SystemFieldEnum } from '../constants/fields'
import { TemplateDetailsStatusEnum } from '../constants/template'
import { createGOFModel, updateGof } from '../factories/GOF'
import FieldsStore from '../store/FieldsStore'
import { GOF, Template } from '../types'
import { UnionField, UnionFieldModelType } from '../types/Field'
import { AssigneeFieldMapping } from '../types/Field/field'

import { Collection } from './Collection'
import GOFModel from './GOFModel'
import WorkflowModel from './WorkflowModel'

export class FieldDoesNotExistError extends Error {
   constructor(message: string) {
      super(message)
   }
}

export class GOFDoesNotExistError extends Error {
   constructor(message: string) {
      super(message)
   }
}

export interface QuickEditFieldsConfig {
   fieldIds: string[]
   showLeadStageQuickEditFields: boolean
}

abstract class TemplateModel {
   readonly templateId: string
   name?: string | undefined | null
   iconUrl?: string | undefined | null
   private gofsCollection: Collection<string, GOFModel>
   private fieldsCollection: Collection<string, UnionFieldModelType>

   private fieldsStore: FieldsStore

   private workflowsCollection: Collection<string, WorkflowModel>

   private _canMergeRecords?: boolean //Note: Resolving this key only in CRM Item Detailed View so its optional

   templateDetailsStatus: TemplateDetailsStatusEnum
   constructor(
      templateId: string,
      name: string | undefined | null,
      fieldsStore: FieldsStore
   ) {
      this.templateId = templateId
      this.name = name
      this.fieldsStore = fieldsStore

      this.templateDetailsStatus = TemplateDetailsStatusEnum.InCompleteDetails

      this.gofsCollection = new Collection<string, GOFModel>(
         'The GOF you are trying to access does not exist'
      )
      this.fieldsCollection = new Collection<string, UnionFieldModelType>(
         'The fields you are trying to access does not exist'
      )
      this.workflowsCollection = new Collection<string, WorkflowModel>(
         'The workflows you are trying to access does not exist'
      )

      makeObservable(this, {
         name: observable,
         templateDetailsStatus: observable
      })
   }

   setIconUrl = (iconUrl: string | null | undefined): void => {
      if (iconUrl) this.iconUrl = iconUrl
   }

   setTemplateDetailsStatus = (
      templateDetailsStatus: TemplateDetailsStatusEnum
   ): void => {
      this.templateDetailsStatus = templateDetailsStatus
   }

   //#region Field

   createOrUpdateField = (gofId: string, field: UnionField): void => {
      const gofModel = this.getOrCreateGOF(gofId)
      this.fieldsStore.createOrUpdateField(field)
      const fieldModel = this.fieldsStore.getField(field.fieldId)
      fieldModel.setGOFModel(gofModel)

      gofModel.fieldsCollection.addItem(fieldModel.fieldId, fieldModel)
      this.fieldsCollection.addItem(fieldModel.fieldId, fieldModel)
   }

   //NOTE: Using this to store the field model if it is already created before
   addFieldModel = (gofId: string, fieldModel: UnionFieldModelType): void => {
      const gofModel = this.getOrCreateGOF(gofId)
      this.fieldsStore.addFieldModel(fieldModel)
      fieldModel.setGOFModel(gofModel)

      gofModel.fieldsCollection.addItem(fieldModel.fieldId, fieldModel)
      this.fieldsCollection.addItem(fieldModel.fieldId, fieldModel)
   }

   getField = (fieldId: string): UnionFieldModelType =>
      this.fieldsCollection.getItem(fieldId)

   getFieldWithSystemFieldEnum = (
      systemFieldEnum: SystemFieldEnum
   ): UnionFieldModelType | undefined =>
      this.fields.find(field => field.systemFieldEnum === systemFieldEnum)

   getGOFWithFieldId = (fieldId: string): GOFModel | undefined =>
      this.gofs.find(gof => gof.fields.find(field => field.fieldId === fieldId))

   hasFieldWithSystemFieldEnum = (systemFieldEnum: SystemFieldEnum): boolean =>
      !!this.fields.find(field => field.systemFieldEnum === systemFieldEnum)

   hasField = (fieldId: string): boolean =>
      this.fieldsCollection.hasItem(fieldId)
   deleteField = (gofId: string, fieldId: string): void => {
      const hasField = this.hasField(fieldId)

      if (!hasField) {
         throw new FieldDoesNotExistError(
            `${fieldId} : The Field you are trying to access is not available.`
         )
      }

      const gofModel = this.getGOF(gofId)

      this.fieldsCollection.deleteItem(fieldId)
      gofModel.fieldsCollection.deleteItem(fieldId)
   }

   get fields(): UnionFieldModelType[] {
      return this.fieldsCollection.orderedItems
   }
   //#endregion

   //#endregion

   //#region GOF

   private getOrCreateGOF = (gofId: string): GOFModel => {
      const isGOFPresent = this.hasGOF(gofId)

      if (isGOFPresent) return this.getGOF(gofId)

      const gofModel = createGOFModel({ gofId, orderedFieldIds: [] }, this)
      this.gofsCollection.addItem(gofModel.gofId, gofModel)

      return gofModel
   }

   private updateGOF = (gofModel: GOFModel, gof: GOF): void => {
      updateGof(gofModel, gof)
   }
   createOrUpdateGOF = (gof: GOF) => {
      const isGofPresent = this.hasGOF(gof.gofId)

      if (!isGofPresent) {
         const gofModel = createGOFModel(gof, this)

         this.gofsCollection.addItem(gofModel.gofId, gofModel)
      }

      const gofModel = this.getGOF(gof.gofId)
      this.updateGOF(gofModel, gof)
   }
   getGOF = (gofId: string): GOFModel => this.gofsCollection.getItem(gofId)
   hasGOF = (gofId: string): boolean => this.gofsCollection.hasItem(gofId)
   deleteGOF = (gofId: string): void => {
      const hasGOF = this.hasGOF(gofId)

      if (!hasGOF) {
         throw new GOFDoesNotExistError(
            `${gofId} : The GOF you are trying to access is not available.`
         )
      }

      this.gofsCollection.deleteItem(gofId)
   }

   get gofs(): GOFModel[] {
      return this.gofsCollection.orderedItems
   }

   get customGofs(): GOFModel[] {
      return this.gofs.filter(gof => gof.gofCategory === GOFCategory.CustomGOF)
   }

   get systemGofs(): GOFModel[] {
      return this.gofs.filter(gof => gof.gofCategory === GOFCategory.SystemGOF)
   }

   //#endregion

   get canMergeRecords(): boolean | undefined {
      return this._canMergeRecords
   }

   setCanMergeRecords(canMergeRecords: boolean): void {
      this._canMergeRecords = canMergeRecords
   }

   abstract update<T extends Template>(template: T): void
}

export default TemplateModel
